import { useState, useEffect } from "react";
import api from "./axiosSetup";
import cfg from "../config";

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
});

// ── Status-Konfiguration ──────────────────────────────────────────────────────
const STATUS_STEPS = [
  { key: "offen",          label: "Offen",           color: "#6c757d" },
  { key: "bestellt",       label: "Bestellt",         color: "#fd7e14" },
  { key: "in Bearbeitung", label: "In Bearbeitung",   color: "#0d6efd" },
  { key: "aufLager",       label: "Auf Lager",        color: "#198754" },
  { key: "ausgegeben",     label: "Ausgegeben",       color: "#146c43" },
  { key: "abgelehnt",      label: "Abgelehnt",        color: "#dc3545" },
];

const getStatusCfg = (state) =>
  STATUS_STEPS.find((s) => s.key === state) ?? { label: state || "Offen", color: "#6c757d" };

// Schritte ohne "abgelehnt" für den Fortschrittsbalken
const PROGRESS_STEPS = STATUS_STEPS.filter((s) => s.key !== "abgelehnt");

const StatusBadge = ({ state }) => {
  const cfg = getStatusCfg(state);
  return (
    <span
      className="badge"
      style={{ backgroundColor: cfg.color, fontSize: "0.85rem", padding: "0.4em 0.75em" }}
    >
      {cfg.label}
    </span>
  );
};

const StatusProgress = ({ state }) => {
  if (state === "abgelehnt") {
    return (
      <div className="mt-2 p-2 rounded text-white text-center small fw-semibold"
        style={{ backgroundColor: "#dc3545" }}>
        Antrag abgelehnt
      </div>
    );
  }

  const currentIdx = PROGRESS_STEPS.findIndex((s) => s.key === state);

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center" style={{ gap: "4px" }}>
        {PROGRESS_STEPS.map((step, i) => {
          const done    = currentIdx >= i;
          const current = currentIdx === i;
          return (
            <div key={step.key} className="d-flex flex-column align-items-center flex-fill">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center mb-1"
                style={{
                  width: 28, height: 28,
                  backgroundColor: done ? step.color : "#dee2e6",
                  border: current ? `2px solid ${step.color}` : "none",
                  fontSize: "0.7rem",
                  color: done ? "#fff" : "#6c757d",
                  fontWeight: "bold",
                }}
              >
                {done ? "✓" : i + 1}
              </div>
              <span
                className="text-center"
                style={{ fontSize: "0.65rem", color: done ? step.color : "#adb5bd", lineHeight: 1.2 }}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
      {/* Verbindungslinie */}
      <div className="position-relative" style={{ marginTop: "-30px", zIndex: 0 }}>
        <div style={{ height: 2, backgroundColor: "#dee2e6", marginLeft: 14, marginRight: 14, marginTop: 14 }} />
      </div>
    </div>
  );
};

// ── Hauptkomponente ───────────────────────────────────────────────────────────
const SRVHaesApplies = () => {
  const [applies, setApplies]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [activeTab, setActiveTab] = useState("offen");

  useEffect(() => {
    api.get("/api/my-data-haes-applies", authHeader())
      .then((r) => setApplies(r.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  
  const open   = applies.filter((a) => !a.done?.value);
  const closed = applies.filter((a) =>  a.done?.value);

  const renderCard = (item) => {
    console.log("mein Häs-Antrag:", item)
    const state       = item.state?.value || "offen";
    const statusCfg   = getStatusCfg(state);
    const hasComment  = item.commentary?.value;
    const extraData   = item.extraData || {};
    const extraKeys   = Object.keys(extraData);
    // Die Beschriftungen kommen vom Typ. Frueher wurde hier der technische
    // Name notduerftig aufgehuebscht, was aus "schuhgroesse" eben
    // "schuhgroesse" machte statt "Schuhgroesse".
    const extraLabels = item.extraLabels || {};

    return (
      <div
        key={item.ID}
        className="card mb-3 shadow-sm border-0"
        style={{ borderLeft: `4px solid ${statusCfg.color}`, borderRadius: 10 }}
      >
        <div className="card-body">

          {/* Header: Typ + Badge */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0 fw-bold">
              {item.Typ?.value || "Unbekannt"}
              {item.childHaes?.value === "Ja" && (
                <span className="badge bg-info text-dark ms-2" style={{ fontSize: "0.7rem" }}>Kinder-{cfg.costumeTerm}</span>
              )}
            </h6>
            <StatusBadge state={state} />
          </div>

          {/* Fortschrittsbalken */}
          <StatusProgress state={state} />

          {/* Basisfelder */}
          <div className="row g-2 mt-2">
            {[
              { label: "Angefragt am",        val: item.createdAt?.value },
              { label: "Gewünschtes Lieferdatum", val: item.lieferdatum?.value },
              { label: "Zubehör",             val: Array.isArray(item.zubehoer?.value) ? item.zubehoer.value.join(", ") || "–" : item.zubehoer?.value || "–" },
            ].map(({ label, val }) =>
              val ? (
                <div className="col-6 col-md-4" key={label}>
                  <div className="p-2 rounded text-center" style={{ backgroundColor: "rgba(255,255,255,0.07)", fontSize: "0.85rem" }}>
                    <div className="text-muted" style={{ fontSize: "0.7rem" }}>{label}</div>
                    <div className="fw-semibold">{val}</div>
                  </div>
                </div>
              ) : null
            )}
          </div>

          {/* Typenspezifische Felder (extraData) */}
          {extraKeys.length > 0 && (
            <div className="mt-3">
              <div className="small text-muted mb-1 fw-semibold">Angaben zum {cfg.costumeTerm}</div>
              <div className="row g-2">
                {extraKeys.map((k) => (
                  <div className="col-6 col-md-4" key={k}>
                    <div className="p-2 rounded text-center" style={{ backgroundColor: "rgba(255,255,255,0.05)", fontSize: "0.85rem" }}>
                      <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                        {extraLabels[k] || k.replace(/_/g, " ")}
                      </div>
                      <div className="fw-semibold">
                        {extraData[k] === true ? "Ja"
                          : extraData[k] === false ? "Nein"
                          : String(extraData[k])}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Kommentar des Zunftrats */}
          {hasComment && (
            <div className="mt-3 p-2 rounded small" style={{ backgroundColor: "rgba(255,200,0,0.1)", borderLeft: "3px solid #ffc107" }}>
              <span className="fw-semibold text-warning">Kommentar {cfg.adminRoleName}: </span>
              {item.commentary.value}
            </div>
          )}

        </div>
      </div>
    );
  };

  if (loading) return <div className="text-center py-3">Lädt…</div>;
  if (error)   return <div className="alert alert-danger">Fehler: {error}</div>;

  return (
    <div>
      <h4 className="mb-3">Meine Anträge ({cfg.costumeTerm})</h4>

      {applies.length === 0 ? (
        <div className="alert alert-info">Keine Anträge vorhanden.</div>
      ) : (
        <>
          <div className="mb-3 d-flex gap-2">
            <button
              className={`btn btn-sm ${activeTab === "offen" ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setActiveTab("offen")}
            >
              Laufend ({open.length})
            </button>
            <button
              className={`btn btn-sm ${activeTab === "abgeschlossen" ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setActiveTab("abgeschlossen")}
            >
              Abgeschlossen ({closed.length})
            </button>
          </div>

          {activeTab === "offen"        && (open.length   === 0 ? <div className="text-muted small">Keine laufenden Anträge.</div> : open.map(renderCard))}
          {activeTab === "abgeschlossen" && (closed.length === 0 ? <div className="text-muted small">Keine abgeschlossenen Anträge.</div> : closed.map(renderCard))}
        </>
      )}
    </div>
  );
};

export default SRVHaesApplies;
